import type { Game, GameProvider } from './api.js';

export interface ProviderSummary {
  id: string;
  code: string | null;
  name: string;
  logoUrl: string | null;
  brandColor: string | null;
  gameCount: number;
}

/**
 * One summary per provider that owns at least one of `games` — pass the games of
 * one bucket (e.g. slots) to get "the camps that have slots". Ordered like
 * `providers` (the admin-set order from /providers); a provider that has games
 * but is missing from that list goes last, by name, labelled from the game's
 * `providerDisplayName`.
 */
export function providersOf(games: readonly Game[], providers: readonly GameProvider[]): ProviderSummary[] {
  const counts = new Map<string, { count: number; name: string }>();
  for (const g of games) {
    const seen = counts.get(g.providerId);
    counts.set(g.providerId, { count: (seen?.count ?? 0) + 1, name: seen?.name || g.providerDisplayName || '' });
  }

  const listed = providers
    .filter((p) => counts.has(p.id))
    .map((p) => ({
      id: p.id,
      code: p.code,
      name: p.displayName || counts.get(p.id)!.name,
      logoUrl: p.logoUrl || null,
      brandColor: p.brandColor || null,
      gameCount: counts.get(p.id)!.count,
    }));

  const listedIds = new Set(listed.map((p) => p.id));
  const unlisted = [...counts]
    .filter(([id]) => !listedIds.has(id))
    .map(([id, { count, name }]) => ({ id, code: null, name, logoUrl: null, brandColor: null, gameCount: count }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return [...listed, ...unlisted];
}
